import React, { useRef, useEffect } from "react";
import Slider from "react-slick";
import { fetchCategories } from "../../redux/features/searchCategorySlice";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Avatar,
  Typography,
  IconButton,
  Stack
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useTranslation } from "react-i18next";

const sliderSettings = {
  dots: false,
  infinite: true,
  // autoplay: true,
  // autoplaySpeed: 1500,
  speed: 500,
  slidesToShow: 6,
  slidesToScroll: 1,
  arrows: false,
  responsive: [
    { breakpoint: 1024, settings: { slidesToShow: 4 } },
    { breakpoint: 768, settings: { slidesToShow: 3 } },
    { breakpoint: 480, settings: { slidesToShow: 2 } },
  ],
};

function Carousel() {
  const { t } = useTranslation()
  const lang = useSelector((state) => state.language.lang);
  const { categories } = useSelector((state) => state.categories);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const sliderRef = useRef(null);

  useEffect(() => {
    dispatch(fetchCategories(lang));
  }, [dispatch, lang]);

  const handleCategoryClick = (categoryId) => {
    dispatch(fetchCategories({ lang, parent_id: categoryId }));
    navigate(`/shops?parent_category_id=${categoryId}&category_id=`);
  };

  return (
    <Box sx={{ py: 4 }}>
      <Box className="container">

        {/* Header + Arrows */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" fontWeight={600}>
            {`${t('Browse_Top_Categories')}`}
          </Typography>

          <Stack direction="row" spacing={1} className="d-none d-lg-flex">
            <IconButton
              onClick={() => sliderRef.current.slickPrev()}
              color="primary"
              size="medium"
            >
              <ArrowBackIosNewIcon fontSize="inherit" sx={{ color: "var(--secondary-light-color) !important" }} />
            </IconButton>
            <IconButton
              onClick={() => sliderRef.current.slickNext()}
              color="primary"
              size="medium"
            >
              <ArrowForwardIosIcon fontSize="inherit" sx={{ color: "var(--secondary-light-color) !important" }} />
            </IconButton>
          </Stack>
        </Stack>

        {/* Slider */}
        <Slider {...sliderSettings} ref={sliderRef}>
          {Array.isArray(categories) &&
            categories.map((category) => (
              <Box key={category.id} px={1} onClick={() => handleCategoryClick(category.id)}>
                <Paper
                  elevation={0}
                  className="d-flex flex-column text-center"
                  sx={{
                    borderRadius: "20px",
                    border: "2px solid transparent",
                    overflow: "hidden",
                    cursor: "pointer",
                    transition: "0.25s",
                    background: "#fff",
                    padding: 1.5,
                    paddingBottom: 0,
                    height: "100%",            // allow flex grid to decide height
                    display: "flex",
                    flexDirection: "column",
                    "&:hover": {
                      borderColor: "var(--secondary-light-color)",
                      boxShadow: 4,
                    }
                  }}
                >
                  <Box
                    component="img"
                    src={category.image}
                    alt="No Image"
                    sx={{
                      width: "100%",
                      height: 140,
                      borderRadius: "20px",
                      objectFit: "cover",
                    }}
                    onError={(e) => {
                      e.target.onerror = null; // prevents infinite loop
                      e.target.src = "https://amzio.de/api/uploads/products/no-thumbnail.png";
                    }}
                  />

                  <Box sx={{ p: 1.5, flexGrow: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      sx={{
                        fontSize: "15px",
                        color: "var(--secondary-light-color)",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                      }}
                      title={category.title} // shows full text on hover
                    >
                      {category.title}
                    </Typography>
                  </Box>
                </Paper>
              </Box>
            ))}
        </Slider>

        {/* Mobile Arrows */}
        <Stack direction="row" justifyContent="center" spacing={2} mt={3} className="d-lg-none">
          <IconButton onClick={() => sliderRef.current.slickPrev()}>
            <ArrowBackIosNewIcon sx={{ color: "var(--secondary-light-color) !important" }} />
          </IconButton>

          <IconButton onClick={() => sliderRef.current.slickNext()}>
            <ArrowForwardIosIcon sx={{ color: "var(--secondary-light-color) !important" }} />
          </IconButton>
        </Stack>
      </Box>
    </Box>
  );
}

export default Carousel;
